import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Library,
  Brain,
  Settings as SettingsIcon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wrench,
  BookOpen,
  BookMarked,
  HelpCircle,
  Users,
  Home,
  Settings,
  ExternalLink,
  MessageSquare,
  Star
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAdmin } from '../../hooks/useAdmin';
import { useSermon } from '../../hooks/useSermon';
import { useTrialReminder } from '../../hooks/useTrialReminder';
import { useSubscription } from '../../hooks/useSubscription';
import { TrialReminderPopup } from '../TrialReminderPopup';
import { NameCompletionModal } from '../NameCompletionModal';
import { DarkModeToggle } from '../DarkModeToggle';
import { FeedbackModal } from '../FeedbackModal';
import { V2InviteBanner } from '../V2InviteBanner';
import { supabase } from '../../lib/supabase';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  // Default to collapsed on mobile using window.innerWidth
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    window.innerWidth < 768
  );
  const [showFeedback, setShowFeedback] = useState(false);
  const { user, signOut } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { isCollaborator, hasSeriesAccess } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const { saveBeforeExit } = useSermon();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { showPopup, dismissPopup, daysLeftInTrial } = useTrialReminder();
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameCheckDone, setNameCheckDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (nameCheckDone) return;

    const checkName = async () => {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        console.log('[NameCheck] User:', user.id, 'Profile result:', profile, 'Error:', profileError);

        if (profileError) {
          console.warn('[NameCheck] Could not fetch profile, will retry on next render');
          return;
        }

        if (profile && (!profile.first_name?.trim() || !profile.last_name?.trim())) {
          console.log('[NameCheck] Name missing, showing modal');
          setShowNameModal(true);
        } else {
          console.log('[NameCheck] Name present:', profile?.first_name, profile?.last_name);
        }
        setNameCheckDone(true);
      } catch (err) {
        console.error('[NameCheck] Error checking profile name:', err);
      }
    };

    const timer = setTimeout(checkName, 500);
    return () => clearTimeout(timer);
  }, [user, nameCheckDone]);

  // Update sidebar state and check mobile on window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsSidebarCollapsed(mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle navigation with save
  const handleNavigation = async (to: string) => {
    // If we're in the editor, save before navigating
    if (location.pathname.includes('/editor')) {
      await saveBeforeExit();
    }

    // Navigate to the new location
    navigate(to);
  };

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div className="min-h-screen dark:bg-midnight" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {showNameModal && user && (
        <NameCompletionModal
          userId={user.id}
          onComplete={() => setShowNameModal(false)}
        />
      )}

      {showPopup && (
        <TrialReminderPopup 
          daysLeft={daysLeftInTrial} 
          onDismiss={dismissPopup}
        />
      )}

      <div className="flex h-screen">
        {/* Desktop Sidebar */}
        <aside className={`bg-white dark:bg-charcoal border-r border-sand/30 dark:border-charcoal shadow-sm transition-all duration-300 ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        } flex flex-col relative`}>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`absolute top-8 bg-white dark:bg-charcoal rounded-full p-1 shadow-md hover:bg-sand/20 dark:hover:bg-charcoal/80 z-10 transition-all ${
              isSidebarCollapsed ? '-right-3' : '-right-3'
            }`}
          >
            {isSidebarCollapsed ? (
              <ChevronRight className="h-4 w-4 text-midnight dark:text-soft-white" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-midnight dark:text-soft-white" />
            )}
          </button>

          <div className={`flex items-center h-16 px-4 border-b border-sand/20 dark:border-charcoal ${isSidebarCollapsed ? 'justify-center px-2' : ''}`}>
            {isSidebarCollapsed ? (
              <img 
                src="/logo-icon.png" 
                alt="SermonBuild Logo" 
                className="h-8 w-8 object-contain" 
              />
            ) : (
              <img 
                src="/logo.png" 
                alt="SermonBuild" 
                className="w-3/4 h-auto object-contain" 
              />
            )}
          </div>

          <nav className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} space-y-2`}>
            <NavLink
              to="/dashboard"
              icon={<Home />}
              label="Dashboard"
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard')}
              exactMatch
            />
            <NavLink
              to="/dashboard/library"
              icon={<Library />}
              label={isCollaborator ? 'Shared Sermons' : 'Sermon Library'}
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/library')}
            />
            <NavLink
              to="/dashboard/research"
              icon={<Brain />}
              label="AI Analysis"
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/research')}
            />
            <NavLink
              to="/dashboard/series-library"
              icon={<BookMarked />}
              label={hasSeriesAccess ? 'Series Library' : 'Series Library 🔒'}
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/series-library')}
            />
            <NavLink
              to="/dashboard/tools"
              icon={<Wrench />}
              label="Tools"
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/tools')}
            />
            {!isCollaborator && (
              <NavLink
                to="/dashboard/collections"
                icon={<BookOpen />}
                label="Collections"
                collapsed={isSidebarCollapsed}
                onClick={() => handleNavigation('/dashboard/collections')}
              />
            )}
            <NavLink
              to="/dashboard/teams"
              icon={<Users />}
              label="Teams"
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/teams')}
            />
            <NavLink
              to="/dashboard/settings"
              icon={<SettingsIcon />}
              label="Settings"
              collapsed={isSidebarCollapsed}
              onClick={() => handleNavigation('/dashboard/settings')}
            />
            {isAdmin && (
              <NavLink
                to="/dashboard/admin"
                icon={<Users />}
                label="Admin"
                collapsed={isSidebarCollapsed}
                onClick={() => handleNavigation('/dashboard/admin')}
              />
            )}
            <a 
              href="https://support.sermonbuild.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`flex items-center p-2 rounded-lg hover:bg-sand/20 dark:hover:bg-charcoal/20 ${
                isSidebarCollapsed ? 'justify-center' : 'space-x-2'
              }`}
            >
              <HelpCircle className="h-5 w-5 text-slate dark:text-soft-white" />
              {!isSidebarCollapsed && <span className="text-slate dark:text-soft-white">Support</span>}
            </a>
          </nav>


          {/* Collaborator upgrade nudge */}
          {isCollaborator && !isSidebarCollapsed && (
            <div className="px-4 mb-2">
              <button
                onClick={() => handleNavigation('/dashboard/settings')}
                className="w-full flex items-center gap-2 p-3 bg-electric/10 border border-electric/20 rounded-lg hover:bg-electric/15 transition-colors"
                title="Upgrade to full premium"
              >
                <Star className="h-4 w-4 text-electric shrink-0" />
                <div className="text-left min-w-0">
                  <div className="text-xs font-semibold text-electric">Collaborator</div>
                  <div className="text-[10px] text-slate truncate">Upgrade for full access</div>
                </div>
              </button>
            </div>
          )}
          {isCollaborator && isSidebarCollapsed && (
            <div className="px-2 mb-2">
              <button
                onClick={() => handleNavigation('/dashboard/settings')}
                className="w-full flex justify-center p-2 bg-electric/10 rounded-lg hover:bg-electric/15 transition-colors"
                title="Collaborator — click to upgrade"
              >
                <Star className="h-5 w-5 text-electric" />
              </button>
            </div>
          )}

          <div className={`mt-auto border-t border-sand/20 dark:border-charcoal ${isSidebarCollapsed ? 'p-2' : 'p-4'}`}>
            {!isSidebarCollapsed && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate dark:text-soft-white">Dark Mode</span>
                <DarkModeToggle size="sm" />
              </div>
            )}
            {isSidebarCollapsed && (
              <div className="flex justify-center mb-3">
                <DarkModeToggle size="sm" />
              </div>
            )}
            <button
              onClick={() => setShowFeedback(true)}
              className={`flex items-center w-full text-slate dark:text-soft-white hover:text-electric dark:hover:text-electric mb-2 ${
                isSidebarCollapsed ? 'justify-center p-2' : 'space-x-2'
              }`}
              title="Send Feedback"
            >
              <MessageSquare className="h-5 w-5" />
              {!isSidebarCollapsed && <span>Feedback</span>}
            </button>
            <button
              onClick={handleSignOut}
              className={`flex items-center hover:text-crimson w-full ${
                isSidebarCollapsed ? 'justify-center p-2' : 'space-x-2'
              }`}
              title="Sign Out"
            >
              <LogOut className="h-5 w-5 text-slate dark:text-soft-white" />
              {!isSidebarCollapsed && <span className="text-slate dark:text-soft-white">Sign Out</span>}
            </button>
          </div>

          {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
        </aside>

        <div className="flex-1 overflow-auto">
          <V2InviteBanner isAdmin={isAdmin} adminLoading={adminLoading} />
          {children}

          {/* Mobile Bottom Navigation */}
          {isMobile && (
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-charcoal border-t border-sand/20 dark:border-charcoal shadow-lg z-50 safe-area-bottom">
              <div className="flex justify-around items-center h-16">
                <NavButton
                  to="/dashboard"
                  icon={<Home className="h-5 w-5" />}
                  label="Home"
                  onClick={() => handleNavigation('/dashboard')}
                  isActive={location.pathname === '/dashboard' || location.pathname === '/'}
                />
                <NavButton
                  to="/dashboard/library"
                  icon={<Library className="h-5 w-5" />}
                  label="Library"
                  onClick={() => handleNavigation('/dashboard/library')}
                  isActive={location.pathname.includes('/library') || location.pathname.includes('/editor')}
                />
                <NavButton
                  to="/dashboard/research"
                  icon={<Brain className="h-5 w-5" />}
                  label="AI"
                  onClick={() => handleNavigation('/dashboard/research')}
                  isActive={location.pathname.includes('/research')}
                />
                <NavButton
                  to="/dashboard/tools"
                  icon={<Wrench className="h-5 w-5" />}
                  label="Tools"
                  onClick={() => handleNavigation('/dashboard/tools')}
                  isActive={location.pathname.includes('/tools')}
                />
                <NavButton
                  to="/dashboard/settings"
                  icon={<SettingsIcon className="h-5 w-5" />}
                  label="Settings"
                  onClick={() => handleNavigation('/dashboard/settings')}
                  isActive={location.pathname.includes('/settings')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  onClick: () => void;
  exactMatch?: boolean;
}

function NavLink({ to, icon, label, collapsed, onClick, exactMatch }: NavLinkProps) {
  const location = useLocation();
  const isActive = exactMatch
    ? (location.pathname === to || location.pathname === '/')
    : location.pathname === to;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center p-2 rounded-lg ${
        isActive
          ? 'bg-gold/10 text-gold'
          : 'text-slate hover:bg-sand/20 dark:hover:bg-charcoal/20 dark:text-soft-white'
      } ${collapsed ? 'justify-center' : 'space-x-2'}`}
      title={collapsed ? label : undefined}
    >
      {React.cloneElement(icon as React.ReactElement, {
        className: `h-5 w-5 ${isActive ? 'text-gold' : 'text-slate dark:text-soft-white'}`,
      })}
      {!collapsed && <span className={isActive ? 'text-gold' : 'text-slate dark:text-soft-white'}>{label}</span>}
    </button>
  );
}

interface NavButtonProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isActive: boolean;
}

function NavButton({ icon, label, onClick, isActive }: NavButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center px-2 py-1"
    >
      <div className={`p-1 rounded-full ${isActive ? 'bg-gold/10 text-gold' : 'text-slate'}`}>
        {icon}
      </div>
      <span className={`text-xs mt-1 ${isActive ? 'text-gold' : 'text-slate'}`}>{label}</span>
    </button>
  );
}